// 소프트 키보드 열림 감지 — 하단 탭바(BottomNav)를 숨기기 위한 전역 훅.
//
// 왜 필요한가(2026-09-03 SE3 실기 · 효신님 "댓글 쓰는 칸이 홈·금융 탭에 막힌다"):
//   iOS 12+ WKWebView는 키보드가 떠도 레이아웃 뷰포트를 줄이지 않고, position:fixed 요소는 키보드 위로
//   재배치된다. 그래서 하단 탭바가 키보드 바로 위에 붙어 포커스된 입력칸을 덮는다. 이 앱엔 Capacitor
//   Keyboard 플러그인이 없고(=native resize 없음), 화면별로 하단 여백을 고쳐 온 패치들은 키보드가 없는
//   상태만 고친 것이라 입력 순간엔 매번 재발했다(매수 CTA portal·살까말까 시트·댓글칸이 같은 뿌리).
//   → 화면 개별 패치가 아니라 "키보드가 열리면 탭바를 치운다"를 한 곳에서 한다.
//
// 감지 2중:
//   ① visualViewport.resize — 키보드가 뜨면 visual viewport 높이가 줄어든다(iOS 13+·Android Chrome).
//   ② focusin/focusout 폴백 — visualViewport 변화가 없거나 늦는 WebView를 위해 텍스트 입력 포커스도 본다.
//   둘 중 하나만 참이어도 "열림". 닫힘은 둘 다 거짓일 때(blur 직후 viewport가 복원되기 전 깜빡임 방지).
import { useEffect, useState } from 'react';

/** 순수 판정 — 테스트용. 창 높이와 visual viewport 높이 차가 임계(기본 120px)를 넘으면 키보드로 본다. */
export function isKeyboardOpenByViewport(innerHeight: number, visualHeight: number, threshold = 120): boolean {
  if (!Number.isFinite(innerHeight) || !Number.isFinite(visualHeight)) return false;
  return innerHeight - visualHeight > threshold;
}

const TEXT_INPUT = 'textarea, input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="number"], input[type="password"], input[type="tel"], input[type="url"], [contenteditable="true"]';

export function isTextInputTarget(t: EventTarget | null): boolean {
  return !!(t && t instanceof Element && t.matches(TEXT_INPUT));
}

export function useKeyboardOpen(): boolean {
  const [byViewport, setByViewport] = useState(false);
  const [byFocus, setByFocus] = useState(false);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const onResize = () => { if (vv) setByViewport(isKeyboardOpenByViewport(window.innerHeight, vv.height)); };
    const onFocusIn = (e: FocusEvent) => { if (isTextInputTarget(e.target)) setByFocus(true); };
    const onFocusOut = (e: FocusEvent) => { if (isTextInputTarget(e.target)) setByFocus(false); };
    vv?.addEventListener('resize', onResize);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    onResize();
    return () => {
      vv?.removeEventListener('resize', onResize);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  const open = byViewport || byFocus;
  // CSS 훅 — 탭바가 사라진 동안 스크롤 컨테이너의 하단 여백(70+safe)도 줄여 입력칸이 화면 끝에 붙게 한다(index.css).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (open) document.documentElement.setAttribute('data-kb-open', '1');
    else document.documentElement.removeAttribute('data-kb-open');
  }, [open]);
  return open;
}
